import React from 'react';
// Leaflet's CSS must be imported before the component tree is rendered so the
// tile layer and controls receive their baseline styles. Vite handles this as
// a side-effect import (no exported symbol needed).
import 'leaflet/dist/leaflet.css';

import L from 'leaflet';
// Vite cannot resolve Leaflet's default marker images via its relative-path
// references inside the leaflet bundle. The standard workaround is to import
// the PNGs as module assets so Vite hashes them, then override the icon
// prototype before any map is mounted.
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { MapContainer, Marker, Popup } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import { BaseTileLayer } from './BaseTileLayer';

// Delete the private _getIconUrl method that Leaflet uses to build its default
// icon paths — after deletion, mergeOptions below takes precedence.
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })
  ._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl, iconUrl, shadowUrl });

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface IncidentMapProps {
  latitude: number | string | null | undefined;
  longitude: number | string | null | undefined;
  /** Optional label shown in the marker popup. */
  locationLabel?: string;
  /** Map container height (CSS value or px number). Default: 240. */
  height?: number | string;
}

// ---------------------------------------------------------------------------
// Helper — parse a latitude/longitude value that may arrive as a string from
// the API (the backend serialises spatial coordinates inconsistently in some
// DTO versions). Returns NaN if the value is absent or not a finite number.
// ---------------------------------------------------------------------------
function parseCoord(value: number | string | null | undefined): number {
  if (value === null || value === undefined || value === '') return NaN;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : NaN;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function IncidentMap({
  latitude,
  longitude,
  locationLabel,
  height = 240,
}: IncidentMapProps): React.ReactElement {
  const { t } = useTranslation();

  const lat = parseCoord(latitude);
  const lng = parseCoord(longitude);

  const hasCoords = !Number.isNaN(lat) && !Number.isNaN(lng);

  if (!hasCoords) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height,
          bgcolor: 'action.hover',
          borderRadius: 1,
          gap: 1,
          px: 2,
        }}
      >
        <LocationOnIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {t('MAP.noCoordinates')}
        </Typography>
      </Box>
    );
  }

  const center: [number, number] = [lat, lng];
  const containerHeight =
    typeof height === 'number' ? `${height}px` : height;

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 0.5 }}
      >
        {t('MAP.title')}
      </Typography>
      <MapContainer
        center={center}
        zoom={15}
        scrollWheelZoom={false}
        style={{ height: containerHeight, width: '100%' }}
      >
        <BaseTileLayer />
        <Marker position={center}>
          {locationLabel ? <Popup>{locationLabel}</Popup> : null}
        </Marker>
      </MapContainer>
    </Box>
  );
}
